/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, uuid, jsonb, timestamp } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { organizations } from "./organizations";

/**
 * Organization Settings table - stores organization-level configuration
 * Used for Velocity branch management, repository defaults, AI settings, and access control
 * 
 * This follows the same pattern as user_settings (see docs/technical/USER_SETTINGS.md)
 * with JSONB columns per settings category
 */
export const organizationSettings = pgTable("organization_settings", {
  organizationId: uuid("organization_id")
    .primaryKey()
    .references(() => organizations.id, { onDelete: 'cascade' }),
  
  // Velocity branch configuration (sync settings, push-to-main strategy)
  velocitySettings: jsonb("velocity_settings"),
  
  // Repository defaults (default branch, product data path, velocity branch name)
  repositorySettings: jsonb("repository_settings"),
  
  // Integration settings (GitHub, GitLab, Jira, Linear, Slack configurations)
  integrationSettings: jsonb("integration_settings"),
  
  // AI configuration (model preferences, resolution settings)
  aiSettings: jsonb("ai_settings"),
  
  // Access control (admin users, sync approvers)
  accessControlSettings: jsonb("access_control_settings"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Insert schema - omit timestamps, require organizationId
export const insertOrganizationSettingsSchema = createInsertSchema(organizationSettings).omit({
  createdAt: true,
  updatedAt: true,
});

// Type exports
export type OrganizationSettings = typeof organizationSettings.$inferSelect;
export type InsertOrganizationSettings = z.infer<typeof insertOrganizationSettingsSchema>;
